import { useState, useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { anilistService } from '@/services/anilist'
import { Anime } from '@/types/anime'
import { LoadingSpinner } from './LoadingSpinner'

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
}

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Anime[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const searchAnime = async () => {
      if (query.trim().length < 2) {
        setResults([])
        return
      }

      setLoading(true)
      try {
        const searchResult = await anilistService.searchAnime(query, 8)
        setResults(searchResult.Page.media)
      } catch (error) {
        console.error('Search error:', error)
        setResults([])
      } finally {
        setLoading(false)
      }
    }

    const debounceTimer = setTimeout(searchAnime, 300)
    return () => clearTimeout(debounceTimer)
  }, [query])

  const handleClose = () => {
    setQuery('')
    setResults([])
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-start justify-center p-4 pt-16">
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
        
        <div className="relative w-full max-w-2xl bg-dark-200 rounded-xl shadow-2xl border border-gray-700">
          {/* Header */}
          <div className="flex items-center p-4 border-b border-gray-700">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search anime..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-dark-300 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleClose}
              className="ml-4 p-2 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : results.length > 0 ? (
              <div className="p-4">
                <div className="grid gap-4">
                  {results.map((anime) => (
                    <Link
                      key={anime.id}
                      to={`/anime/${anime.id}`}
                      onClick={handleClose}
                      className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-800 transition-colors group"
                    >
                      <div className="relative w-16 h-20 flex-shrink-0">
                        <img
                          src={anime.coverImage.medium}
                          alt={anime.title.romaji}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white group-hover:text-blue-400 transition-colors truncate">
                          {anime.title.english || anime.title.romaji}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          {anime.averageScore && (
                            <span className="text-sm text-green-400">
                              {anime.averageScore}%
                            </span>
                          )}
                          {anime.seasonYear && (
                            <span className="text-sm text-gray-400">
                              {anime.seasonYear}
                            </span>
                          )}
                          {anime.episodes && (
                            <span className="text-sm text-gray-400">
                              {anime.episodes} eps
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {anime.genres.slice(0, 3).map((genre) => (
                            <span
                              key={genre}
                              className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded"
                            >
                              {genre}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : query.trim().length >= 2 ? (
              <div className="flex items-center justify-center py-8 text-gray-400">
                No anime found for "{query}"
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-gray-400">
                Start typing to search anime...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
